<template>
    <div :class="isModal
        ? 'absolute right-0 top-[120%] w-72 rounded-[28px] border border-[#e4ccb4] bg-[#fff8f1] p-4 shadow-[0_10px_40px_rgba(0,0,0,0.12)] z-50 max-h-scroll'
        : 'w-full static bg-transparent p-0 border-0 shadow-none'"
    >
        <div v-if="isModal" class="flex items-center gap-3 border-b border-[#ead7c4] pb-4 mb-4">
            <circular-button :type="'BORDER'" :textContent="authStore.getUser?.getInitialsOfName() || 'UN'" />

            <div>
                <p class="font-bold text-[#7d140f] text-sm leading-tight">
                    {{ authStore.getUser?.getName() }}
                </p>
                <p class="text-xs text-[#8a6a5b] break-all">
                    {{ authStore.getUser?.email }}
                </p>
            </div>
        </div>

        <div class="flex flex-col gap-1">
            <router-link v-for="(item, index) of getRoutes()" :key="index" :to="item.url"
                class="rounded-xl px-4 py-3 text-sm font-semibold text-[#6a3328] hover:bg-[#f3e5d4] transition-colors flex items-center gap-2">
                {{ translate(item.label, 'capitalize') }}
            </router-link>

            <router-link to="/dashboard/profile"
                class="rounded-xl px-4 py-3 text-sm font-semibold text-[#6a3328] hover:bg-[#f3e5d4] transition-colors">
                {{ translate('account configuration', 'capitalize') }}
            </router-link>

            <button 
                @click="context.openLanguageModal" 
                class="rounded-xl px-4 py-3 text-left text-sm font-semibold text-[#6a3328] hover:bg-[#f3e5d4] transition-colors w-full">
                {{ translate('language', 'ucfirst') }}
            </button>

            <button @click="logout"
                class="rounded-xl px-4 py-3 text-left text-sm font-semibold text-[#6a3328] hover:bg-[#f3e5d4] transition-colors w-full">
                {{ translate('logout', 'ucfirst') }}
            </button>
        </div>

        <change-language-component :visible="context.isLanguageModalOpen" @closed="context.closeLanguageModal" />
    </div>
</template>

<script setup>
import { defineProps } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { translate } from '@/composables/translate';
import AuthUserService from '@/services/AuthUserService';
import { showToast } from '@/composables/toast';
import urlHandler from '@/composables/urlHandler';
import { useToast } from 'primevue';
import CircularButton from '../misc/CircularButton.vue';
import ChangeLanguageComponent from '../Utils/ChangeLanguageComponent.vue';
import { useContextStore } from '@/stores/context.js';

defineProps({
    isModal: {
        type: Boolean,
        default: true,
    }
});

const toast = useToast();
const authStore = useAuthStore();
const context = useContextStore();

const menuOptions = {
    ADMIN: [
        { url: '/admin/dashboard/', label: 'courses' },
        { url: '/admin/instructors/', label: 'instructors' },
        { url: '/admin/testimonies/', label: 'testimonies' },
        { url: '/admin/categories/', label: 'categories' },
        { url: '/admin/blog/', label: 'blog' },
        { url: '/admin/about/', label: 'about post' },
        { url: '/admin/students/', label: 'students' },
        { url: '/admin/students_payments/', label: 'student payments' },
        { url: '/admin/exclusive_material', label: 'exclusive materials' },
        { url: '/admin/selling_material', label: "teacher's materials" },
        { url: '/admin/support/', label: 'support callings' }
    ],
    USER: [
        { url: '/dashboard/', label: 'my learning' },
        { url: '/dashboard/teacher_materials/', label: "teacher's materials" },
        { url: '/dashboard/materials', label: 'exclusive materials', condition: 'HAS_STUDENT' },
        { url: '/dashboard/payment_history/', label: 'purchase history' },
        { url: '/dashboard/support/', label: 'help and support' }
    ]
};

/**
 * Gets routes
 * @returns {Array}
 */
function getRoutes() {
    const routes = authStore.isAdmin ? menuOptions.ADMIN : menuOptions.USER;

    let allowedRoutes = [];

    for (let route of routes) {
        if (canUseRoute(route?.condition)) {
            allowedRoutes.push(route);
        }
    }

    return allowedRoutes;
}

function canUseRoute(condition = null) {
    const user = authStore.getUser;

    switch (condition) {
        case 'HAS_STUDENT':
            return user.student != null && user.student.isAllowed();

        default:
            return true;
    }
}

async function logout() {
    await AuthUserService.logout();

    showToast(toast, translate('logged out', 'ucfirst'), 'info');

    urlHandler.redirectAndScroll('/');
}
</script>